import { HospitalityMenuCategoryItem } from "@/lib/types";
import { getHospitalityMenuCategoryItemFile, uploadHospitalityMenuCategoryItemFile } from "@/sideEffects/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileUploadDropzone } from "@/components/upload/FileUploadDropzone";
import { showApiError } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { FileLoading, HoverPen, ImageDimensionClass, Placeholder } from "@/components/upload/FileUploadHelpers";
import { getContentFullUrl } from "@/lib/contentUtils";

interface Props {
  categoryItem: HospitalityMenuCategoryItem;
}

const dropzoneOptions = {
  accept: {
    "image/*": [],
  },
  multiple: false,
  maxFiles: 1,
};

export const UpdateHospitalityMenuCategoryItemMetaFormImage: React.FC<Props> = ({ categoryItem }) => {
  const { data: menuFileData, isLoading: isMenuFileLoading } = useQuery({
    queryKey: ["getHospitalityMenuCategoryItemFile", categoryItem.id],
    queryFn: () =>
      getHospitalityMenuCategoryItemFile(categoryItem.id, {
        numberOfObjectsPerPage: 1,
        pageNumber: 1,
      }),
  });

  const menuFile = menuFileData?.data?.result?.[0];

  const queryClient = useQueryClient();
  const uploadFileMutation = useMutation({
    mutationFn: (file: File) => uploadHospitalityMenuCategoryItemFile(categoryItem.id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getHospitalityMenuCategoryItemFile", categoryItem.id],
      });
    },
    onError: showApiError,
  });

  if (isMenuFileLoading) {
    return <FileLoading />;
  }

  return (
    <FileUploadDropzone
      onClickUpload={(files) => uploadFileMutation.mutate(files[0])}
      isLoading={uploadFileMutation.isPending}
      dropzoneOptions={dropzoneOptions}
      dropZoneContent={
        <div className="hover:opacity-50 cursor-pointer relative inline-block">
          {menuFile ? (
            <img
              src={getContentFullUrl(menuFile.contentStorageName)}
              className={cn(ImageDimensionClass, "object-contain")}
            />
          ) : (
            <Placeholder />
          )}
          <HoverPen />
        </div>
      }
    />
  );
};
